import { useNavigate } from 'react-router-dom';

import { Button } from 'fluxo-ui';
import { AwardIcon, BarChartIcon, CalendarIcon, CheckCircleIcon, ShieldIcon, UserGroupIcon } from 'fluxo-ui/icons';

import { ROUTES } from '../common/constants';
import { useAuthStore } from '../store/authStore';

const features = [
        {
            icon: CalendarIcon,
            title: 'Create & Manage Exams',
            description: 'Build comprehensive assessments with various question types and advanced settings',
        },
        {
            icon: UserGroupIcon,
            title: 'Live Quiz Mode',
            description: 'Engage participants with real-time Kahoot-style quizzes and instant leaderboards',
        },
        {
            icon: BarChartIcon,
            title: 'Advanced Analytics',
            description: 'Track performance with detailed analytics and insights on exam and question levels',
        },
        {
            icon: CheckCircleIcon,
            title: 'AI-Powered Grading',
            description: 'Automatic grading for subjective questions using advanced AI models',
        },
        {
            icon: ShieldIcon,
            title: 'Advanced Proctoring',
            description: 'Fullscreen enforcement, focus monitoring, and anti-cheating safeguards for every attempt',
        },
        {
            icon: AwardIcon,
            title: 'Certificates & Verification',
            description: 'Auto-generate certificates on exam pass with unique verification codes',
        },
];

const stats = [
    { value: '20+', label: 'Question types' },
    { value: 'AI', label: 'Assisted grading' },
    { value: 'Live', label: 'Real-time quizzes' },
    { value: '24/7', label: 'Proctored exams' },
];

const HomePage: React.FC = () => {
    const navigate = useNavigate();
    const { isAuthenticated, user } = useAuthStore();

    return (
        <div>
            {/* Hero Section */}
            <section className="relative overflow-hidden bg-slate-50 dark:bg-slate-950">
                {/* Decorative background */}
                <div
                    aria-hidden
                    className="absolute inset-0 bg-[radial-gradient(circle,rgba(59,130,246,0.08)_1px,transparent_1px)] bg-[size:24px_24px]"
                />
                <div aria-hidden className="absolute -top-32 -left-32 w-96 h-96 rounded-full bg-teal-400/20 dark:bg-teal-600/10 blur-3xl" />
                <div
                    aria-hidden
                    className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-cyan-400/20 dark:bg-cyan-600/10 blur-3xl"
                />

                <div className="relative max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-24 sm:py-28">
                    <div className="text-center">
                        <span className="inline-block mb-6 px-4 py-1.5 rounded-full text-sm font-medium bg-teal-50 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 border border-teal-100 dark:border-teal-900/50">
                            Assessments, live quizzes & analytics — in one place
                        </span>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 dark:text-white mb-6">
                            Welcome to{' '}
                            <span className="bg-gradient-to-r from-teal-600 to-cyan-600 bg-clip-text text-transparent">Testam</span>
                        </h1>
                        <p className="text-lg sm:text-xl text-slate-600 dark:text-slate-300 mb-10 max-w-3xl mx-auto">
                            Comprehensive quiz and exam management platform for creating, conducting, and analyzing assessments with
                            advanced proctoring and AI-powered features.
                        </p>

                        {isAuthenticated && user ? (
                            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                                <Button label="Go to Dashboard" variant="primary" size="lg" onClick={() => navigate(ROUTES.DASHBOARD)} />
                                <Button
                                    label="Create New Exam"
                                    variant="default"
                                    layout="outlined"
                                    size="lg"
                                    onClick={() => navigate(ROUTES.EXAM_CREATE)}
                                />
                            </div>
                        ) : (
                            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                                <Button label="Get Started" variant="primary" size="lg" onClick={() => navigate(ROUTES.SIGNUP)} />
                                <Button
                                    label="Sign In"
                                    variant="default"
                                    layout="outlined"
                                    size="lg"
                                    onClick={() => navigate(ROUTES.LOGIN)}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Stats band */}
            <section className="bg-gradient-to-r from-teal-600 to-cyan-600">
                <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                        {stats.map((stat) => (
                            <div key={stat.label}>
                                <div className="text-3xl sm:text-4xl font-bold text-white mb-1">{stat.value}</div>
                                <div className="text-sm text-teal-100">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="py-20 bg-slate-50 dark:bg-slate-950">
                <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-14">
                        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-white mb-3">
                            Powerful Features
                        </h2>
                        <p className="text-base sm:text-lg text-slate-500 dark:text-slate-400">
                            Everything you need to create and manage assessments
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {features.map((feature, index) => {
                            const Icon = feature.icon;
                            return (
                                <div
                                    key={index}
                                    className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md hover:border-teal-200 dark:hover:border-teal-900 transition-shadow p-6"
                                >
                                    <div className="w-10 h-10 rounded-lg bg-teal-50 dark:bg-teal-900/30 text-teal-600 dark:text-teal-400 flex items-center justify-center mb-4">
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">{feature.title}</h3>
                                    <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">{feature.description}</p>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* CTA Section (for non-authenticated users) */}
            {!isAuthenticated && (
                <section className="relative overflow-hidden bg-gradient-to-r from-teal-600 to-cyan-600">
                    <div
                        aria-hidden
                        className="absolute inset-0 bg-[radial-gradient(circle,rgba(255,255,255,0.08)_1px,transparent_1px)] bg-[size:24px_24px]"
                    />
                    <div className="relative max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
                        <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-4">Ready to get started?</h2>
                        <p className="text-lg sm:text-xl text-teal-100 mb-8 max-w-2xl mx-auto">
                            Create your free account today and start building assessments
                        </p>
                        <Button label="Sign Up Now" variant="default" size="lg" onClick={() => navigate(ROUTES.SIGNUP)} />
                    </div>
                </section>
            )}
        </div>
    );
};

export default HomePage;
